import { useMemo } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { useHidden } from "contexts/hidden";
import { useHighlightCollections } from "contexts/highlightCollections";
import { useHighlighted } from "contexts/highlighted";
import { useSelectionBasket } from "contexts/selectionBasket";
import { selectCoordinates } from "features/coordinates";
import { selectMeasureEntities } from "features/measure/measureSlice";
import { selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { selectPointLines } from "features/pointLine/pointLineSlice";
import { ObjectVisibility, selectClippingPlanes, selectDefaultVisibility } from "features/render";

/**
 * Returns true when there is at least one scene modification the Reset button
 * could clear (measurements, section planes, hidden/highlighted objects, xray).
 */
export function useHasSceneModifications(): boolean {
    const coordinates = useAppSelector(selectCoordinates);
    const lasers = useAppSelector(selectOutlineLasers);
    const pointLines = useAppSelector(selectPointLines);
    const measureEntities = useAppSelector(selectMeasureEntities);
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const defaultVisibility = useAppSelector(selectDefaultVisibility);

    const hidden = useHidden();
    const highlighted = useHighlighted();
    const selectionBasket = useSelectionBasket();
    const highlightCollections = useHighlightCollections();

    return useMemo(() => {
        if (coordinates.length > 0) {
            return true;
        }
        if (lasers.length > 0) {
            return true;
        }
        if (pointLines.some((pl) => pl.points.length > 0)) {
            return true;
        }
        if (measureEntities.some((set) => set.length > 0)) {
            return true;
        }
        if (clippingPlanes.planes.length > 0) {
            return true;
        }
        if (defaultVisibility !== ObjectVisibility.Neutral) {
            return true;
        }
        if (hidden.idArr.length > 0) {
            return true;
        }
        if (highlighted.idArr.length > 0) {
            return true;
        }
        if (selectionBasket.idArr.length > 0) {
            return true;
        }
        if (Object.values(highlightCollections).some((c) => c.idArr.length > 0)) {
            return true;
        }
        return false;
    }, [
        coordinates,
        lasers,
        pointLines,
        measureEntities,
        clippingPlanes,
        defaultVisibility,
        hidden.idArr,
        highlighted.idArr,
        selectionBasket.idArr,
        highlightCollections,
    ]);
}
